import { useMemo } from 'react';
import { Button, Link } from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import styles from './eval-list-table.module.scss';

/**
 * A table row: the row `id` (used as the DataTable row key + passed to `onRowClick`) plus its
 * display-cell values (all strings). Index signature so it satisfies the DataTable row contract.
 */
export type EvalListRow = { id: string } & Record<string, string>;

/** One column definition (key → the row field, header → the display label). */
export interface EvalListColumn {
  key: string;
  header: string;
}

interface EvalListTableProps {
  headers: readonly EvalListColumn[];
  rows: EvalListRow[];
  /** Load state → DataTable loading (`loading`) / message + Retry (`error`) / the table (`ready`). */
  status: 'loading' | 'error' | 'ready';
  /** Message shown when `status === 'error'`. */
  errorMessage?: string;
  /** Retry handler for the error state. */
  onRetry?: () => void;
  /** Which column renders as the navigating link — the only clickable element in the row. */
  linkColumnKey: string;
  /** Fired when the link cell is activated (click / Enter / Space) with the row's `id`. */
  onRowClick: (id: string) => void;
}

/**
 * The interactive DataTable features are all off — this is a static, read-only list, so no
 * sorting / search / column customization / selection / pagination toolbar.
 */
const READ_ONLY_CONFIG = {
  sorting: false,
  globalSearch: false,
  columnFilters: false,
  columnVisibility: false,
  columnReordering: false,
  columnResizing: false,
  columnPinning: false,
} as const;

/**
 * A read-only eval-list table — the house {@link DataTable} (`components/data-table`) in its
 * static, no-scroll mode. The `linkColumnKey` column renders a keyboard-accessible link
 * (`<Link as="button">`) that navigates via `onRowClick`; every other column is plain text. The
 * DataTable owns the loading + empty states; the error(+Retry) state is a small status block
 * (it's a message, not a table). Purely presentational — the caller fetches, maps rows, and does
 * the navigation in `onRowClick`.
 *
 * Shared by `WorkspacePage` (a workspace's evals) and `SavedEvalsPage` (the package's saved
 * evals); the only differences are the columns, the data source, and where a row routes — all
 * props. (Migrated from a raw Carbon `DataTable` to the house component per PR #6986 review;
 * navigation is now the link cell rather than a whole-row click.)
 *
 * @param {EvalListTableProps} props - Columns, rows, load status, the link column, and the row-click handler.
 * @return {JSX.Element} The table (or its loading / error state).
 */
export function EvalListTable({
  headers,
  rows,
  status,
  errorMessage,
  onRetry,
  linkColumnKey,
  onRowClick,
}: EvalListTableProps) {
  // The link column gets a `<Link as="button">` cell that fires `onRowClick(id)` — a real,
  // keyboard-accessible button styled as a link; every other column is plain text.
  const columns = useMemo<DataTableColumnDef<EvalListRow>[]>(
    () =>
      headers.map((column) => {
        if (column.key !== linkColumnKey) {
          return { accessorKey: column.key, header: column.header };
        }
        return {
          accessorKey: column.key,
          header: column.header,
          cell: (info) => (
            <Link
              as="button"
              type="button"
              onClick={() => onRowClick(info.row.original.id)}
            >
              {info.getValue<string>()}
            </Link>
          ),
        };
      }),
    [headers, linkColumnKey, onRowClick]
  );

  if (status === 'error') {
    return (
      <div className={styles.evalListTable}>
        <div className={styles.statusBlock}>
          <span className={styles.statusText}>
            {errorMessage ?? 'Couldn’t load evaluations.'}
          </span>
          {onRetry && (
            <Button kind="tertiary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.evalListTable}>
      {/* Cap the list height and scroll rows within it (sticky header) rather than growing to
          fit every row. */}
      <div className={styles.tableScroll}>
        <DataTable<EvalListRow>
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          isLoading={status === 'loading'}
          config={READ_ONLY_CONFIG}
        />
      </div>
    </div>
  );
}
